<template>
  <section class="hero">
    <div class="hero-body">
      <div class="container" v-if="shoppingList">
      <div class="notification is-danger" v-if="error">{{error}}</div>
        <h1 class="title">
          <span
            v-if="!editTitleMode"
            @mouseover="showEditTitleButton = true"
            @mouseout="showEditTitleButton = false">
            {{ name }}
            <button v-if="isMobile() || showEditTitleButton"
              class="button is-white disable-hover"
              @click="editTitleEnabled">

              <b-icon
                pack="fas"
                icon="pencil-alt"
                size="is-small" />
            </button>
          </span>
          <div v-else class="control">
            <input
              class="input title-input"
              type="text"
              ref="titleInput"
              v-model="newTitle"
              :placeholder="name"
              @keyup.enter="editTitleOk"
              @keyup.esc="editTitleCancel">

            <button class="button is-white" @click="editTitleOk">
              <b-icon
                pack="fas"
                icon="check"
                size="is-small" />
            </button>

            <button class="button is-white" @click="editTitleCancel">
              <b-icon
                pack="fas"
                icon="times"
                size="is-small" />
            </button>
          </div>
        </h1>
        <template v-if="shoppingList.modifiedAt">
          <h2 class="subtitle modified-at" :style="modifiedAtStyle">
            {{$t('lastModified')}} {{lastModified}} &nbsp;
            <b-icon v-if="synching"
              pack="fas"
              icon="spinner"
              size="is-small"
              custom-class="fa-pulse" />
          </h2>
          <p>
            <b-tooltip :label="$t('clearAll')" :active="showTooltips" position="is-bottom">
              <button class="button" @click="clearAll">
                <b-icon
                  pack="fas"
                  icon="trash-alt"
                  size="is-small" />
              </button>
            </b-tooltip>

            <b-tooltip :label="undoTooltip" :active="showTooltips" position="is-bottom">
              <button
                :disabled="undoStack.length === 0"
                class="button"
                @click="undoLastAction">
                <b-icon 
                  pack="fas"
                  icon="undo-alt"
                  size="is-small" />
              </button>
            </b-tooltip>

            <b-tooltip :label="redoTooltip" :active="showTooltips" position="is-bottom">
              <button
                :disabled="redoStack.length === 0"
                class="button"
                @click="redoLastAction">
                <b-icon
                  pack="fas"
                  icon="redo-alt"
                  size="is-small" />
              </button>
            </b-tooltip>
          </p>
        </template>
        <b-icon v-else-if="synching"
          pack="fas"
          icon="spinner"
          size="is-small"
          custom-class="fa-pulse" />
        <hr>
        <div v-if="shoppingList && foodItems" class="content">
          <div class="columns is-variable is-8">
            <div class="column is-narrow shopping-list-column">
              <shopping-list-item-list
                :name="$t('items')"
                :checked="false"
                v-model="uncheckedItemList"
                @drag-start="drag.active = 'unchecked'"
                @drag-end="drag.active = null"
                @drag-move="evt => drag.evt = evt"
                @checkbox="(args) => triggerAction(Actions.CheckItemAction, args)"
                @change="changeUncheckedItem"
                @delete="index => triggerAction(Actions.DeleteItemAction, { index, itemListName: 'unchecked' })" />
              <p/>
              <form v-on:submit.prevent="addNewItem()">
                <div class="field has-addons">
                  <p class="control is-expanded">
                    <input
                      ref="newItemDescription"
                      v-model="newItemDescription"
                      class="input is-expanded"
                      type="text"
                      :placeholder="$t('newItem')">
                  </p>
                  <p class="control">
                    <button role="button" class="button is-primary">Add</button>
                  </p>
                </div>
              </form>
            </div>
            <div class="column is-narrow shopping-list-column">
              <shopping-list-item-list
                :name="$t('purchased')"
                :checked="true"
                v-model="checkedItemList"
                @drag-start="drag.active = 'checked'"
                @drag-end="drag.active = null"
                @drag-move="evt => drag.evt = evt"
                @checkbox="(args) => triggerAction(Actions.UncheckItemAction, args)"
                @change="changeCheckedItem"
                @delete="index => triggerAction(Actions.DeleteItemAction, { index, itemListName: 'checked' })" />
            </div>
          </div>
        </div>
      </div>

      <div class="container" v-else-if="error">
        <h1 class="title">Invalid shopping list</h1>
      </div>
    </div>
  </section>
</template>

<i18n src="../../i18n/ShoppingList.i18n.json"></i18n>

<script>
import moment from 'moment-timezone';
import i18nMixin from '@/mixins/i18n';
import routingMixin from '@/mixins/routing';
import { mapState, mapMutations } from 'vuex';
import {
  map,
  findIndex,
  get as lodashGet,
  cloneDeep,
} from 'lodash';
import { mixin as undoRedoMixin } from './ShoppingListUndoRedo';
import ShoppingListItem from '../../shared/ShoppingListItem';
import ShoppingListItemList from './ShoppingListItemList';
import * as queries from '../../graphql/shopping-list';
import * as Actions from './actions';

export default {
  name: 'shoppingList',
  mixins: [i18nMixin, undoRedoMixin, routingMixin],
  props: ['id'],
  data() {
    return {
      error: null,
      shoppingList: null,
      synching: true,
      shoppingListSub: null,
      showTooltips: !this.isMobile(),

      showEditTitleButton: false,
      editTitleMode: false,
      newTitle: null,
      newItemDescription: null,

      idToQuery: this.id,

      drag: {
        active: null,
        evt: null,
      },

      modifiedAtStyle: {
        color: null,
      },
    };
  },
  watch: {
    $route() {
      this.idToQuery = this.id;

      // Not sure why this is necessary. If I don't do it,
      // the variables function in the query is called, but
      // the actual fetch is not made with the new id.

      // https://github.com/Akryum/vue-apollo/issues/262
      this.$apollo.queries.shoppingList.refetch(() => ({ id: this.id }));
    },
  },
  apollo: {
    shoppingList: {
      query: queries.LIST_QUERY,
      variables() {
        return {
          id: this.idToQuery,
        };
      },
      result(apolloQueryResult) {
        if (apolloQueryResult.loading ||
            apolloQueryResult.networkStatus !== 7) {
          return;
        }

        const id = lodashGet(apolloQueryResult.data, 'shoppingList.id');
        if (!id) {
          this.error = 'Invalid id';
          return;
        }

        this.routeShoppingList({
          id,
          noBack: true,
        });

        this.error = '';
        this.defaultShoppingListId(id);
        this.currentShoppingList(apolloQueryResult.data.shoppingList);

        this.fetchFoodItems().then(() => this.subscribeToChanges());
      },
      error(error) {
        this.handleError(error);
      },
    },
  },
  methods: {
    ...mapMutations('shoppingList', ['defaultShoppingListId', 'currentShoppingList']),
    fetchFoodItems() {
      const dupCheck = new Set();
      const ids = [];
      const pullIds = (item) => {
        if (item.foodItemId && !dupCheck.has(item.foodItemId)) {
          ids.push(item.foodItemId);
          dupCheck.add(item.foodItemId);
        }
      };

      map(this.shoppingList.items.checked, pullIds);
      map(this.shoppingList.items.unchecked, pullIds);

      return this.$store.dispatch('shoppingList/fetchFoodItems', ids)
      .then(() => {
        this.synching = false;
      })
      .catch((err) => {
        this.error = err.message;
        throw err;
      });
    },
    subscribeToChanges() {
      if (this.shoppingListSub) {
        this.shoppingListSub.unsubscribe();
      }

      const self = this;

      // Subscribe to shopping list updates
      this.shoppingListSub = this.$apollo.queries.shoppingList.subscribeToMore({
        document: queries.CHANGED,
        variables: {
          id: this.shoppingList.id,
        },
        updateQuery: (previousResult, { subscriptionData }) => {
          /* eslint-disable no-underscore-dangle */
          let actionName;
          let index;
          let postAction;

          // TODO check subscriptionData.errors first

          const updateResult = lodashGet(subscriptionData, 'data.shoppingListChanged');
          switch (updateResult.__typename) {
            case 'ShoppingListCheckItemResult': {
              index = findIndex(self.shoppingList.items.unchecked,
                { id: updateResult.item.id });
              if (index < 0) {
                throw new Error('Received update for missing unchecked item');
              }

              actionName = 'CheckItemAction';
              break;
            }

            case 'ShoppingListUncheckItemResult': {
              index = findIndex(self.shoppingList.items.checked,
                { id: updateResult.item.id });
              if (index < 0) {
                throw new Error('Received update for missing checked item');
              }

              actionName = 'UncheckItemAction';
              break;
            }

            case 'ShoppingListAddItemsResult': {
              postAction = () => self.fetchFoodItems();
              actionName = 'AddItemsAction';
              break;
            }

            case 'ShoppingListAddItemResult': {
              index = findIndex(self.shoppingList.items.unchecked,
                { id: updateResult.item.id });
              if (index >= 0) {
                throw new Error('Received update to add already existing item');
              }

              index = updateResult.index;
              actionName = 'AddItemAction';
              break;
            }

            case 'ShoppingListDeleteItemResult': {
              index = findIndex(self.shoppingList.items[updateResult.itemListName],
                { id: updateResult.itemId });
              if (index < 0) {
                throw new Error('Received update to delete missing item');
              }

              actionName = 'DeleteItemAction';
              break;
            }

            case 'ShoppingListChangeItemResult': {
              index = findIndex(self.shoppingList.items[updateResult.itemListName],
                { id: updateResult.item.id });
              if (index < 0) {
                throw new Error('Received update to change missing item');
              }

              actionName = 'ChangeItemAction';
              break;
            }

            case 'ShoppingListClearAllResult': {
              actionName = 'ClearAllAction';
              break;
            }

            case 'ShoppingListMoveItemResult': {
              actionName = 'MoveItemAction';
              break;
            }

            case 'ShoppingListUpdateNameResult': {
              actionName = 'UpdateNameAction';
              break;
            }

            default:
              actionName = null;
          }

          if (actionName) {
            const cloneResult = cloneDeep(previousResult);
            const action = new Actions[actionName]({
              component: self,
              ...updateResult,
              index,
            });

            action.updateQuery(cloneResult.shoppingList, updateResult);
            if (postAction) {
              this.$nextTick(postAction);
            }

            self.modifiedAtStyle.color = 'green';
            setTimeout(() => { self.modifiedAtStyle.color = null; }, 500);

            self.$toast.open({
              message: self.$t('receivedUpdate'),
              type: 'is-info',
            });

            self.clearUndoRedo();
            return cloneResult;
          }

          return previousResult;
        },
        onError: (err) => {
          /* eslint-disable no-console */
          console.error(err);
          this.error = err.message;
        },
      });
    },
    editTitleEnabled() {
      this.newTitle = this.shoppingList.name;
      this.editTitleMode = true;
      this.$nextTick(() => {
        const input = this.$refs.titleInput;
        input.focus();
        input.setSelectionRange(this.newTitle.length, this.newTitle.length);
      });
    },
    editTitleOk() {
      if (this.newTitle !== this.shoppingList.name) {
        this.triggerAction(Actions.UpdateNameAction, {
          value: this.newTitle,
        }, { throwOnError: true }).then(() => {
          this.newTitle = '';
          this.editTitleMode = false;
        }).catch(() => {
          this.$nextTick(() => {
            const input = this.$refs.titleInput;
            input.focus();
            input.setSelectionRange(0, this.newTitle.length);
          });
        });
      } else {
        this.editTitleMode = false;
      }

      this.showEditTitleButton = false;
    },
    editTitleCancel() {
      this.showEditTitleButton = false;
      this.editTitleMode = false;
      this.error = '';
    },
    changeUncheckedItem(index, item, value) {
      return this.triggerAction(Actions.ChangeItemAction, {
        itemListName: 'unchecked',
        index,
        item,
        value,
      });
    },
    changeCheckedItem(index, item, value) {
      return this.triggerAction(Actions.ChangeItemAction, {
        itemListName: 'checked',
        index,
        item,
        value,
      });
    },
    addNewItem() {
      if (!this.newItemDescription) {
        return;
      }

      const newItem = new ShoppingListItem({
        description: this.newItemDescription,
      });

      if (findIndex(this.shoppingList.items.unchecked, { externalId: newItem.externalId }) >= 0) {
        this.$toast.open({
          message: 'Already exists',
          type: 'is-warning',
        });

        this.$nextTick(() => {
          const input = this.$refs.newItemDescription;
          input.focus();
          input.setSelectionRange(0, this.newItemDescription.length);
        });

        return;
      }

      this.triggerAction(Actions.AddItemAction, { item: newItem }).then(() => {
        this.newItemDescription = '';
      });
    },
    clearAll() {
      this.$dialog.confirm({
        title: this.$t('clearShoppingList'),
        message: this.$t('confirmClearAll'),
        confirmText: this.$t('clearButton'),
        cancelText: this.$t('cancelButton'),
        type: 'is-danger',
        hasIcon: true,

        onConfirm: () => {
          this.triggerAction(Actions.ClearAllAction);
        },
      });
    },
    triggerAction(ActionType, actionArgs, options) {
      const action = new ActionType({
        component: this,
        ...actionArgs,
      });

      return action.execute().then((result) => {
        if (actionArgs && actionArgs.undoAction) {
          this.pushRedo(actionArgs.undoAction);
        } else if (typeof action.undo === 'function') {
          this.pushUndo({ undoAction: action });
        } else {
          this.clearUndoRedo();
        }

        this.error = null;
        return result;
      }).catch((err) => {
        this.handleError(err);
        if (options && options.throwOnError) {
          throw err;
        }
      });
    },
    undoLastAction() {
      return this.$store.dispatch('shoppingList/popUndo')
        .then(action => action.undo())
        .then(() => { this.error = null; })
        .catch((err) => { this.error = err.message; });
    },
    redoLastAction() {
      return this.$store.dispatch('shoppingList/popRedo')
        .then(action =>
          action.execute().then(() => {
            this.pushUndo({ undoAction: action, isRedoAction: true });
            this.error = null;
          })
        )
        .catch((err) => {
          this.error = err.message;
        });
    },
    handleError(error) {
      const innerError = error.graphQLErrors[0] || error.networkError || error;
      this.error = innerError.message;
    },
  },
  computed: {
    ...mapState('shoppingList', ['foodItems']),
    name() {
      return this.shoppingList.name === 'default' ? this.$i18n.t('shoppingList') : this.shoppingList.name;
    },
    lastModified() {
      if (!this.shoppingList.modifiedAt) {
        return null;
      }

      const lm = moment(this.shoppingList.modifiedAt);
      const lmDate = lm.clone().startOf('day');
      let format = '';
      if (lmDate.isSame(moment().startOf('day'))) {
        format = 'LT';
      } else if (this.isMobile()) {
        format = 'l';
      } else {
        format = 'l LT';
      }

      return lm.format(format);
    },
    uncheckedItemList: {
      get() {
        return this.shoppingList.items.unchecked;
      },
      set(value) {
        this.triggerAction(Actions.MoveItemAction, {
          drag: this.drag,
          itemListName: 'unchecked',
          value,
        });
      },
    },
    checkedItemList: {
      get() {
        return this.shoppingList.items.checked;
      },
      set(value) {
        this.triggerAction(Actions.MoveItemAction, {
          drag: this.drag,
          itemListName: 'checked',
          value,
        });
      },
    },
    Actions: {
      get() {
        return Actions;
      },
    },
  },
  components: {
    ShoppingListItemList,
  },
};
</script>

<style lang="sass" scoped>
@import "~bulma/sass/utilities/_all"
@import "~bulma/sass/elements/form.sass"

.shopping-list-column
  min-width: 20rem
  @include tablet
    min-width: 30rem

.shopping-list-delete
  float: right

.items-leave-active
  text-decoration: line-through
  color: hsl(0, 0%, 96%)
  border: 0px
  transition: 0.5s
  opacity: 0

.items-enter
  opacity: 0

.items-enter-active  
  transition: all 0.5s

.items-move
  transition: transform 0.5s

.modified-at
  transition: color 0.5s

.disable-hover:hover
  background-color: white

.title-input
  width: 15rem
  @include mobile
    width: 10rem

</style>



// WEBPACK FOOTER //
// src/components/shopping-list/ShoppingList.vue